import type { User } from 'firebase/auth';

import type { RoomReferenceData } from '@elara/domain/models/schemas/shortlist-schemas';

import type { RoomOptionsContainer } from '#containers/RoomOptionsContainer.js';
import type { TripsContainer } from '#containers/TripsContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { KeyValueStore } from '#lib/key-value-store.js';
import type { ViewportPort } from '#lib/viewport-port.js';
import type { FlightOptionModel } from '#models/FlightOptionModel.js';
import type { Trip, TripModel } from '#models/TripModel.js';
import type { GuideTarget, GuideViewParams } from './guide-stack.js';
import type { NavigationPort } from './navigation-port.js';
import type { Route, TripPanelType } from './routing-contract.js';
import type { SessionState } from './SessionState.js';
import type { ToastState } from './ToastState.js';

import { effect } from '@preact/signals-react';

import { isPreviewType } from '@elara/domain/schemas/preview-schemas';
import { where } from '@elara/firebolt/shared';

import type { ShareMethod } from '#lib/analytics-events.js';

import {
  trackItineraryPrinted,
  trackItineraryShared,
  trackTripCreated,
  trackTripDeleted,
  trackTripShareDisabled,
  trackTripShareEnabled,
} from '#lib/analytics-events.js';
import { ROUTES } from '#constants.js';
import { extractDatePart, toDate } from '#lib/date-utils.js';
import { getErrorReporter } from '#lib/error-reporting.js';
import { EMPTY_QUERY } from '#lib/firebolt-utils.js';
import { generatePageTitle } from '#lib/page-title.js';
import { parseHashFragment } from '#lib/route-utils.js';
import { createState } from '#lib/signals-utils.js';

import { clearTripChatDrafts } from './chat-draft-storage.js';
import { isTripPanel, isTripRoutePath, tripPanelPath } from './routing-contract.js';

/** Handle type for direct trip document access */
export interface TripDocHandle {
  isReady: boolean;
  data: Trip | undefined;
  unsubscribe(): void;
}

// `TripPanelType` is part of the shared routing contract (the active panel is carried in the URL
// path, ELARA-704). Re-exported so existing `#states/TripsState.js` importers are unchanged.
export type { TripPanelType } from './routing-contract.js';

/** Persistence key for the itinerary builder collapsed flag. */
const ITINERARY_BUILDER_COLLAPSED_KEY = 'itinerary-builder-collapsed';

/**
 * The main reactive store for all core trip functionality.
 *
 * @remark All the getters in FlatStore are transformed into computed signals, the values are signals.
 * The functions are not touched, they are the actions on the state.
 */
export function createTripsState(
  sessionState: SessionState,
  trips: TripsContainer,
  routerState: NavigationPort,
  toastState: ToastState,
  keyValueStore: KeyValueStore,
  viewport: ViewportPort,
  /** Reads a room's hotel and search, so a room guide opens over its hotel's Rooms tab. */
  roomOptions: Pick<RoomOptionsContainer, 'ClientRoomOption'>,
) {
  /** True for both `/trip/:id` and the panel form `/trip/:id/:panel` (ELARA-704). */
  const isTripPath = isTripRoutePath;

  /** The active panel carried in the route path, or `undefined` on a bare `/trip/:id`. */
  const panelFromRoute = (route: Route): TripPanelType | undefined => {
    const raw = route.params.panel;
    return raw && isTripPanel(raw) ? raw : undefined;
  };

  const state = createState({
    /** The currently active/selected trip instance. `undefined` if no trip is selected. */
    current: undefined as ClientInstanceOf<TripModel> | undefined,
    /** Flag to prevent duplicate trip creation when on /trip/new route */
    isCreatingNewTrip: false,
    /** Whether the rename trip modal is open */
    isRenameDialogOpen: false,
    /** Whether the delete trip modal is open */
    isDeleteDialogOpen: false,
    /** Whether the date picker modal is open */
    isDateDialogOpen: false,
    /** Whether the share-itinerary dialog (web) / sheet (mobile) is open */
    isShareDialogOpen: false,
    /** True while an enable/disable share-link flow is in flight */
    isUpdatingShareLink: false,
    /** The new trip name for the rename form */
    newTripName: '',
    /** The start date for the date picker form (YYYY-MM-DD format) */
    newStartDate: '',
    /** The end date for the date picker form (YYYY-MM-DD format) */
    newEndDate: '',
    /** The currently visible panel on mobile, or the main panel on desktop */
    activePanel: 'chat' as TripPanelType,
    /** The panel whose nav icon is currently pulsing (null = no animation) */
    animatingPanel: null as TripPanelType | null,
    /** @internal Timer handle for animation auto-clear */
    _animationTimer: undefined as ReturnType<typeof setTimeout> | undefined,
    /** Whether the app is running on mobile (screen width < 768px) */
    isMobile: false,
    /**
     * Whether the itinerary builder sidebar is collapsed.
     *
     * Defaults to `false` and is hydrated from persistence in {@link hydrate}
     * (called by `startMonitors`). It must NOT read storage in this initializer:
     * doing so crashes the entire states container on platforms without the DOM
     * `localStorage` global (React Native) — see ELARA-691.
     */
    isItineraryBuilderCollapsed: false,
    /** Whether the preview panel is open */
    isPreviewPanelOpen: false,
    /** The title of the preview panel */
    previewPanelTitle: '',
    /** Preview data passed directly (for button-triggered previews) */
    previewData: undefined as
      | { type: 'room'; data: RoomReferenceData }
      | { type: 'flight'; data: FlightOptionModel }
      | undefined,
    /** Collection of cleanup functions for all effects (instance-scoped) */
    cleanupFunctions: [] as Array<() => void>,
    /** Cleanup function reference to ensure idempotency (instance-scoped) */
    cleanupFunctionRef: undefined as (() => void) | undefined,
    /**
     * Handle for direct trip document access (used for privileged access to other users' trips).
     * This is separate from recentTrips which only shows the current user's trips.
     */
    privilegedTripHandle: undefined as TripDocHandle | undefined,
    /**
     * The trip ID currently being loaded via privileged access.
     * Used to prevent duplicate load attempts.
     */
    _privilegedLoadingTripId: undefined as string | undefined,

    /** The current trip's public share token, or `undefined` while sharing is off. Build URLs with `buildSharedItineraryUrl`. */
    get shareToken(): string | undefined {
      return this.current?.shareLink?.token ?? undefined;
    },

    /** Whether the signed-in user may share the current trip. See {@link canShareTrip}. */
    get canShareItinerary(): boolean {
      return this.canShareTrip(this.current);
    },

    /** A computed signal that returns `true` if a trip has been loaded. */
    get hasLoaded() {
      return !!this.current;
    },

    /**
     * Returns `true` if the current user is viewing another user's trip (privileged access).
     * This is determined by comparing the trip's userId to the current session userId.
     */
    get isViewingOtherUserTrip(): boolean {
      if (!this.current || !sessionState.userId) return false;
      return this.current.userId !== sessionState.userId;
    },

    /** A computed signal that returns a reactive query for the user's recent trips. */
    get recentTrips() {
      if (!sessionState.userId) return EMPTY_QUERY;
      return trips.ClientTrip.list({}, where('userId', '==', sessionState.userId), where('status', '!=', 'archived'));
    },

    get nextTrip(): ClientInstanceOf<TripModel> | undefined {
      if (!this.recentTrips.isReady) return undefined;
      const now = new Date();

      // Filter upcoming trips and sort by start date
      const upcomingTrips = this.recentTrips.items
        .filter((t: ClientInstanceOf<TripModel>) => {
          if (!t.startDate) return false;
          if (t.isDemo) return false;
          const tripStartDate = toDate(t.startDate);
          return tripStartDate !== undefined && tripStartDate > now;
        })
        .toSorted((a, b) => {
          const aDate = toDate(a.startDate);
          const bDate = toDate(b.startDate);
          return (aDate?.getTime() ?? Infinity) - (bDate?.getTime() ?? Infinity);
        });

      return upcomingTrips[0];
    },

    /** A computed signal that returns whether a panel should be visible based on current state */
    get isPanelVisible() {
      return (panelType: TripPanelType) => {
        if (this.isMobile) {
          // On mobile, only show the active panel
          return this.activePanel === panelType;
        } else {
          // On desktop, show chat always, and the active panel
          return panelType === 'chat' || this.activePanel === panelType;
        }
      };
    },

    /**
     * Whether the guest can see the chat. Only when it is the active panel: on desktop every other
     * panel takes the full width and fades the chat out, even though `isPanelVisible('chat')` keeps it
     * mounted (so its scroll position survives).
     */
    get isChatShown(): boolean {
      return this.activePanel === 'chat';
    },

    /** A computed signal that returns the available panels for the current view */
    get availablePanels() {
      return ['chat', 'shortlist', 'itinerary', 'bookings'] as TripPanelType[];
    },

    /** Selects a trip by ID from the recentTrips list */
    selectTripById(tripId: string) {
      if (this.recentTrips.isReady) {
        if (this.current?.id === tripId) return true;
        else this.reset();
        const trip = this.recentTrips.items.find((t: ClientInstanceOf<TripModel>) => t.id === tripId);
        if (trip) {
          this.current = trip;
          return true;
        }
      }
      return false;
    },

    /**
     * Load a trip directly by ID for privileged users.
     * This bypasses the recentTrips query which only shows the current user's trips.
     * Used when an admin/dev navigates to another user's trip URL.
     *
     * @param tripId - The ID of the trip to load
     */
    loadTripDirectly(tripId: string): void {
      // Guard: Don't reload if we're already loading or have loaded this trip
      if (this._privilegedLoadingTripId === tripId) {
        return;
      }

      // Clean up previous privileged handle if exists (for a DIFFERENT trip)
      // Use the cleanup function to ensure consistent state reset
      this.cleanupPrivilegedTripHandle();

      console.info(`[TripsState] Loading trip directly for privileged access: ${tripId}`);

      // Track which trip we're loading to prevent duplicate loads
      this._privilegedLoadingTripId = tripId;

      // Create a direct document handle for the trip
      // This will work if the user has read access (privileged users via Firestore rules)
      const handle = trips.ClientTrip.get({ id: tripId });
      this.privilegedTripHandle = handle;

      // The effect in startMonitors will watch for when this becomes ready
      // and set this.current from the handle data
    },

    /**
     * Cleanup the privileged trip handle subscription
     */
    cleanupPrivilegedTripHandle(): void {
      if (this.privilegedTripHandle) {
        this.privilegedTripHandle.unsubscribe();
        this.privilegedTripHandle = undefined;
      }
      this._privilegedLoadingTripId = undefined;
    },

    /**
     * Reset trip-specific UI state.
     * @param preservePreview - When switching trips, pass URL preview params so reset does not close-then-reopen the preview panel (avoids flicker).
     */
    reset(preservePreview?: { type: string; id: string }) {
      // Clean up privileged trip handle subscription
      this.cleanupPrivilegedTripHandle();
      // Reset any trip-specific state
      this.isRenameDialogOpen = false;
      this.isDeleteDialogOpen = false;
      this.isDateDialogOpen = false;
      this.newTripName = '';
      this.newStartDate = '';
      this.newEndDate = '';
      this.activePanel = 'chat';
      this.previewPanelTitle = '';
      if (preservePreview) {
        this.isPreviewPanelOpen = true;
        this.previewData = undefined;
      } else {
        this.isPreviewPanelOpen = false;
        this.previewData = undefined;
      }
    },

    /** Switch to a different panel */
    switchToPanel(panelType: TripPanelType, onSwitched?: () => void) {
      // Switch the visible panel immediately (pure in-place state update — keeps tab switching
      // snappy, independent of the router round-trip). The URL is then updated to the canonical
      // panel path for shareability/reload; `replace` keeps panel switches out of the back stack
      // (ELARA-704).
      this.activePanel = panelType;
      const currentRoute = routerState.currentRoute;
      const tripId = currentRoute?.params.id;
      if (currentRoute && tripId && isTripPath(currentRoute.path)) {
        routerState.navigate(tripPanelPath(tripId, panelType), { replace: true });
      }

      // Execute callback after panel has switched and React has rendered
      // Use double requestAnimationFrame to ensure DOM has updated
      if (onSwitched) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            onSwitched();
          });
        });
      }
    },

    /** Pulse a nav icon to draw attention (e.g. when an item is added to shortlist or itinerary) */
    pulseNavIcon(panelType: TripPanelType) {
      // Clear any previous animation
      if (this._animationTimer) {
        clearTimeout(this._animationTimer);
      }
      // Reset to null first so re-trigger restarts the CSS animation
      this.animatingPanel = null;
      requestAnimationFrame(() => {
        this.animatingPanel = panelType;
        this._animationTimer = setTimeout(() => {
          this.animatingPanel = null;
          this._animationTimer = undefined;
        }, 700);
      });
    },

    /**
     * Hydrate persisted UI state from the {@link KeyValueStore} port.
     *
     * Separated from construction so the store can be built before any platform
     * persistence is touched (the hydrate()-then-render pattern, ELARA-691).
     * Idempotent and safe to call multiple times.
     */
    hydrate() {
      this.isItineraryBuilderCollapsed = keyValueStore.get(ITINERARY_BUILDER_COLLAPSED_KEY) === 'true';
    },

    /** Toggle the itinerary builder collapsed state */
    toggleItineraryBuilderCollapsed() {
      this.isItineraryBuilderCollapsed = !this.isItineraryBuilderCollapsed;
      keyValueStore.set(ITINERARY_BUILDER_COLLAPSED_KEY, String(this.isItineraryBuilderCollapsed));
    },

    /** Open the preview panel */
    openPreviewPanel() {
      this.isPreviewPanelOpen = true;
      this.previewData = undefined;
    },

    /** Close the preview panel, ending its guide stack. */
    closePreviewPanel() {
      routerState.closeGuide();
      this.isPreviewPanelOpen = false;
      this.previewData = undefined;
    },

    /** Toggle the preview panel */
    togglePreviewPanel() {
      this.isPreviewPanelOpen = !this.isPreviewPanelOpen;
    },

    /** Show destination guide preview */
    showDestinationGuide(destinationId: string) {
      this.showGuide('destination', destinationId);
    },

    /** Show hotel guide preview */
    showHotelGuide(hotelId: string) {
      this.showGuide('hotel', hotelId);
    },

    /** Show place guide preview (for restaurants, experiences, etc.) */
    showPlaceGuide(placeId: string) {
      this.showGuide('place', placeId);
    },

    /**
     * Open a guide from outside any guide (chat, shortlist, itinerary, stories): a new guide stack.
     * Links followed INSIDE a guide use {@link pushGuide} instead, so Back returns to the guide they
     * came from. `view` opens the guide on a part of itself — the hotel guide's Rooms tab on a given
     * search (ELARA-1092). The route change handler opens the panel.
     */
    showGuide(type: string, id: string, view?: GuideViewParams) {
      if (!isPreviewType(type)) return;
      const target = { type, id, ...(view ? { view } : {}) };
      const parent = type === 'room' ? this.roomParentGuide(id) : undefined;
      if (parent) routerState.openGuide(target, 'reset', { parent });
      else routerState.openGuide(target, 'reset');
    },

    /**
     * The guide a room opens over: its hotel's Rooms tab, on the search that found the room, so Back
     * from a room opened anywhere (chat, the shortlist, the itinerary) leads to that search's other
     * rooms. The hotel's name labels the breadcrumb before its guide has rendered. Undefined when the
     * room is not loaded yet (a cold link, with nothing on screen showing it) or has no hotel: it then
     * opens alone, as before.
     */
    roomParentGuide(roomId: string): GuideTarget | undefined {
      const tripId = this.current?.id;
      if (!tripId) return;
      const room = roomOptions.ClientRoomOption.get({ tripId, id: roomId }).data;
      if (!room?.hotelId) return;
      const hotelName = room.hotel?.name;
      return {
        type: 'hotel',
        id: room.hotelId,
        view: room.searchId ? { tab: 'rooms', search: room.searchId } : { tab: 'rooms' },
        ...(hotelName ? { title: hotelName } : {}),
      };
    },

    /** Open a guide from a link inside another guide, so Back returns to it. */
    pushGuide(type: string, id: string) {
      if (!isPreviewType(type)) return;
      routerState.openGuide({ type, id }, 'push');
    },

    /**
     * Swap the open guide for another without a history entry — a redirect (a place that is a
     * hotel), so Back never lands on a guide that sends you straight on again. Also moves a guide to
     * another view of itself (the hotel guide's Overview / Rooms tabs): the stack entry carries the
     * view, so Back from a guide opened on top returns to the tab the traveller left.
     */
    replaceGuide(type: string, id: string, view?: GuideViewParams) {
      if (!isPreviewType(type)) return;
      routerState.openGuide({ type, id, ...(view ? { view } : {}) }, 'replace');
    },

    /** Open preview panel with data (flight only; room preview is via showGuide('room', id)) */
    openPreviewWithData(
      type: 'room' | 'destination' | 'hotel' | 'place' | 'flight',
      data:
        | RoomReferenceData
        | { destinationId: string }
        | { hotelId: string }
        | { placeId: string }
        | FlightOptionModel,
    ) {
      this.previewData = undefined;
      if (type === 'flight') {
        this.previewData = { type: 'flight', data: data as FlightOptionModel };
      }
      this.isPreviewPanelOpen = true;
    },

    /** Update mobile detection based on screen size */
    updateMobileDetection() {
      this.isMobile = viewport.getWidth() < 768;
    },

    /** Open the rename trip modal */
    openRenameDialog(currentTripName: string) {
      this.newTripName = currentTripName;
      this.isRenameDialogOpen = true;
    },

    /** Close the rename trip modal */
    closeRenameDialog() {
      this.isRenameDialogOpen = false;
      this.newTripName = '';
    },

    /** Open the delete trip modal */
    openDeleteDialog() {
      this.isDeleteDialogOpen = true;
    },

    /** Close the delete trip modal */
    closeDeleteDialog() {
      this.isDeleteDialogOpen = false;
    },

    /**
     * Whether the signed-in user may share `trip`'s itinerary: only its owner, and never an archived
     * trip (the server enforces the same rules). Demo trips are shareable — they belong to the user.
     */
    canShareTrip(trip: Pick<Trip, 'userId' | 'isArchived'> | undefined): boolean {
      if (!trip || !sessionState.userId || trip.userId !== sessionState.userId) return false;
      return !trip.isArchived;
    },

    /** Open the share-itinerary dialog */
    openShareDialog() {
      this.isShareDialogOpen = true;
    },

    /** Close the share-itinerary dialog */
    closeShareDialog() {
      this.isShareDialogOpen = false;
    },

    /**
     * Turns on the trip's public read-only itinerary link. Returns the token (the trip listener also
     * delivers it via {@link shareToken}), or `undefined` after showing an error toast.
     */
    async enableShareLink(tripId: string): Promise<string | undefined> {
      const { ClientTrip } = trips;
      this.isUpdatingShareLink = true;
      try {
        const {
          data: { token },
        } = await ClientTrip.enableShareLink({ tripId });
        trackTripShareEnabled({ tripId });
        return token;
      } catch (error) {
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Couldn’t create link',
          description: 'Please try again.',
          variant: 'destructive',
        });
        return undefined;
      } finally {
        this.isUpdatingShareLink = false;
      }
    },

    /** Revokes the trip's public itinerary link. Returns whether it succeeded (an error toast is shown otherwise). */
    async disableShareLink(tripId: string): Promise<boolean> {
      const { ClientTrip } = trips;
      this.isUpdatingShareLink = true;
      try {
        await ClientTrip.disableShareLink({ tripId });
        trackTripShareDisabled({ tripId });
        return true;
      } catch (error) {
        getErrorReporter().captureException(error);
        toastState.addToast({
          title: 'Couldn’t turn off link',
          description: 'Please try again.',
          variant: 'destructive',
        });
        return false;
      } finally {
        this.isUpdatingShareLink = false;
      }
    },

    /** Records that the owner passed the link on (copied it or used the OS share sheet). */
    recordItineraryShared(tripId: string, method: ShareMethod) {
      trackItineraryShared({ tripId, method });
    },

    /** Records that the owner printed the trip's itinerary from its Print button. */
    recordItineraryPrinted(tripId: string) {
      trackItineraryPrinted({ tripId });
    },

    /** Open the date picker modal */
    openDateDialog(currentStartDate?: string, currentEndDate?: string) {
      this.newStartDate = currentStartDate ? extractDatePart(currentStartDate) || '' : '';
      this.newEndDate = currentEndDate ? extractDatePart(currentEndDate) || '' : '';
      this.isDateDialogOpen = true;
    },

    /** Close the date picker modal */
    closeDateDialog() {
      this.isDateDialogOpen = false;
      this.newStartDate = '';
      this.newEndDate = '';
    },

    /** Update the start date */
    setNewStartDate(date: string) {
      this.newStartDate = date;
    },

    /** Update the end date */
    setNewEndDate(date: string) {
      this.newEndDate = date;
    },

    /** Update the new trip name */
    setNewTripName(name: string) {
      this.newTripName = name;
    },

    /** Update the title of the preview panel; it also labels the guide in the panel's breadcrumb. */
    setPreviewPanelTitle(title: string) {
      this.previewPanelTitle = title;
      routerState.setGuideTitle(title);
    },

    /** Check if the rename form is valid */
    get isRenameFormValid(): boolean {
      return this.newTripName.trim().length > 0;
    },

    /** Check if the date form is valid */
    get isDateFormValid(): boolean {
      // At minimum, we need a start date
      if (!this.newStartDate) return false;
      // If we have an end date, validate that it's after or equal to start date
      if (this.newEndDate) {
        const start = new Date(this.newStartDate);
        const end = new Date(this.newEndDate);
        return end >= start;
      }
      // If only start date is set, that's valid (we'll use it for both start and end)
      return true;
    },

    clearCurrentTrip() {
      this.current = undefined;
      // Update URL to reflect no trip selected
      routerState.navigate(ROUTES.HOME);
    },

    /** Handles the logic for creating a new trip session. */
    async onNewTripAction({
      currentUser,
      onCreated,
    }: {
      currentUser: User;
      /**
       * Overrides the post-create navigation. By default the store navigates to the trip route
       * (`/trip/:id`, replacing history), which on web opens on the chat. Platforms whose trip route
       * lands somewhere else supply this to own the navigation themselves — native uses it to land in
       * the chat panel with the Trip Overview beneath it (so Back goes chat → overview → home). Called
       * only on success, after the trip is loaded directly; the failure path always returns Home.
       */
      onCreated?: (tripId: string) => void;
    }) {
      const { ClientTrip } = trips;

      // Set flag to prevent duplicate creation attempts
      this.isCreatingNewTrip = true;

      try {
        console.debug(`[TripsState] Starting a new trip for user: ${currentUser.uid}`);
        const result = await ClientTrip.create({ userData: { name: currentUser.displayName || 'User' } });
        const newTripData = result.data;
        if (newTripData) {
          const tripId = newTripData.id;
          console.debug(`[TripsState] Trip created successfully: ${tripId}`);
          trackTripCreated({ tripId });

          // Navigate immediately using direct document handle.
          // The recentTrips listener will catch up in the background;
          // we don't wait for it to avoid the timeout race condition.
          this.loadTripDirectly(tripId);
          if (onCreated) {
            onCreated(tripId);
          } else {
            routerState.navigate(`/trip/${tripId}`, { replace: true });
          }
        }

        this.isCreatingNewTrip = false;
      } catch (error) {
        console.error('[TripsState] Failed to create new trip', { error, userId: currentUser.uid });
        getErrorReporter().captureException(error);

        this.isCreatingNewTrip = false;

        toastState.addToast({
          title: 'Failed to create trip',
          description: 'Unable to start a new trip. Please try again.',
          variant: 'destructive',
        });

        routerState.navigate(ROUTES.HOME, { replace: true });
      }
    },

    /** Handles the logic for deleting (archiving) a trip session. */
    async deleteTrip(tripId: string) {
      const { ClientTrip } = trips;
      try {
        // If this is the currently selected trip, clear it first
        if (this.current?.id === tripId) {
          this.clearCurrentTrip();
        }

        // Call the delete flow
        await ClientTrip.delete({ tripId });
        trackTripDeleted({ tripId });

        // The flow archives every chat in the trip, so their unsent composer drafts are now
        // unreachable — and this is the last moment the client can act on them, since it has already
        // stopped watching the trip (ELARA-969).
        clearTripChatDrafts(keyValueStore, tripId);
      } catch (error) {
        getErrorReporter().captureException(error);
        throw error; // Re-throw so the UI can handle it
      }
    },

    /** Handles the logic for restoring a previously archived trip session. */
    async restoreTrip(tripId: string) {
      const { ClientTrip } = trips;
      try {
        await ClientTrip.restore({ tripId });
      } catch (error) {
        getErrorReporter().captureException(error);
        throw error; // Re-throw so the UI can handle it
      }
    },

    /** Update trip details */
    async updateTrip(
      tripId: string,
      updates: { title?: string; destinationIds?: string[]; startDate?: string | null; endDate?: string | null },
    ) {
      const { ClientTrip } = trips;
      try {
        await ClientTrip.update({ ...updates, tripId });
      } catch (error) {
        getErrorReporter().captureException(error);
        throw error; // Re-throw so the UI can handle it
      }
    },

    /** Handle route changes */
    handleRouteChange(route: Route) {
      // Parse hash fragment to check for preview params
      const hashParams = route.hash ? parseHashFragment(route.hash) : {};
      const previewType = hashParams.preview;
      const previewId = hashParams.id;

      // Update preview panel state based on hash params. Hotel/destination/place guides are
      // route-agnostic (they read global models), so they may preview on any route; room/flight are
      // trip-scoped (their docs live under trips/{tripId}/...) and only preview on a trip route.
      const isTripRoute = isTripPath(route.path);
      const needsTrip = previewType === 'room' || previewType === 'flight';
      const canPreview = !!previewType && !!previewId && (isTripRoute || !needsTrip);

      if (canPreview) {
        // Valid preview params exist, open preview panel
        // Clear any button-triggered preview data when URL-based preview is triggered
        this.previewData = undefined;
        this.isPreviewPanelOpen = true;
      } else if (!isTripRoute) {
        // Off-trip with no previewable params — ensure closed and clear trip-scoped button data
        this.isPreviewPanelOpen = false;
        this.previewData = undefined;
      } else if (!this.previewData) {
        // On trip route but no URL params and no button-triggered preview (e.g. room/flight)
        this.isPreviewPanelOpen = false;
        this.previewData = undefined;
      }
      // else: on trip route with previewData (e.g. room/flight opened from shortlist) — keep panel open when switching tabs

      // Check if we're on the new trip route
      const isNewTripRoute = route.path === ROUTES.TRIP_NEW;

      if (isNewTripRoute) {
        // Create a new trip if we're on /trip/new and not already creating one
        if (sessionState.user && this.recentTrips.isReady && !this.isCreatingNewTrip) {
          this.onNewTripAction({ currentUser: sessionState.user }).catch((error) => {
            console.error('[TripsState] Trip creation failed in handleRouteChange', error);
          });
        }
        return;
      }

      // Reset the creation flag when we leave the new trip route
      this.isCreatingNewTrip = false;

      if (isTripRoute && route.params.id) {
        const tripId = route.params.id;
        // Don't navigate, just select the trip directly to avoid infinite loop
        if (this.recentTrips.isReady) {
          if (this.current?.id === tripId) {
            // Same trip, just update panel from the URL path if present
            const panelFromUrl = panelFromRoute(route);
            if (panelFromUrl) {
              this.activePanel = panelFromUrl;
            }
            return;
          }

          // Check if we're already loading this trip via privileged access
          const isAlreadyLoadingPrivileged = this._privilegedLoadingTripId === tripId;

          // First, check if trip exists in user's trips
          const trip = this.recentTrips.items.find((t: ClientInstanceOf<TripModel>) => t.id === tripId);

          if (trip) {
            // Trip found in user's own trips
            const panelFromUrl = panelFromRoute(route);
            const targetPanel = panelFromUrl ?? 'chat';

            // Reset UI if switching to a different trip
            if (this.current?.id !== tripId) {
              // Use queueMicrotask to avoid signal cycle, then set panel AFTER reset
              queueMicrotask(() => {
                const hashParams = route.hash ? parseHashFragment(route.hash) : {};
                const preservePreview =
                  hashParams.preview && hashParams.id && isTripPath(route.path)
                    ? { type: hashParams.preview, id: hashParams.id }
                    : undefined;
                this.reset(preservePreview);
                // Set panel after reset to prevent it being overwritten. A bare /trip/:id defaults
                // to chat without rewriting the URL — navigating here would re-enter this handler
                // (see the "avoid infinite loop" note above) and flip the matched route. Explicit
                // panel URLs come from user actions via switchToPanel (ELARA-704).
                this.activePanel = targetPanel;
              });
            } else {
              // Same trip, just update panel
              this.activePanel = targetPanel;
            }

            this.current = trip;
            // Clear any privileged loading state since we found the trip in user's trips
            this._privilegedLoadingTripId = undefined;
          } else if (isAlreadyLoadingPrivileged) {
            // Trip is already being loaded via direct document access (newly created or privileged).
            // Don't interfere -- Effect 4 in startMonitors will set this.current when the handle resolves.
            const panelFromUrl = panelFromRoute(route);
            if (panelFromUrl) {
              this.activePanel = panelFromUrl;
            }
          } else if (sessionState.isPrivileged) {
            // Trip not found in user's trips, but user is privileged
            // Try direct document access to view another user's trip
            console.info(`[TripsState] Trip ${tripId} not in user's trips, attempting privileged access`);
            this.loadTripDirectly(tripId);
            const panelFromUrl = panelFromRoute(route);
            if (panelFromUrl) {
              this.activePanel = panelFromUrl;
            }
          } else {
            // Not privileged, trip not found
            console.warn(`[TripsState] Trip ${tripId} not found and user is not privileged`, {
              recentTripsCount: this.recentTrips.items.length,
              isPrivileged: sessionState.isPrivileged,
              userId: sessionState.userId,
              userEmail: sessionState.user?.email,
            });
            console.info('[TripsState] Clearing stale trip state after access check', { tripId });
            // Clear current trip immediately to avoid stale content flashing
            this.current = undefined;
            // Reset UI state (dialogs, preview, panel) after clearing trip
            // Use queueMicrotask to avoid signal cycles
            queueMicrotask(() => {
              this.reset();
            });
          }
        }
      } else {
        // Not on trip route - clean up privileged handle and reset
        queueMicrotask(() => this.cleanupPrivilegedTripHandle());
        this.current = undefined;
      }
    },

    /** Start monitoring route changes (idempotent - safe to call multiple times) */
    startMonitors() {
      // Return existing cleanup function if already started
      if (this.cleanupFunctionRef) {
        return this.cleanupFunctionRef;
      }

      // Hydrate persisted UI state once, before effects render against it.
      this.hydrate();

      // Register all effects
      const effects = [
        // Effect 1: Handle route changes and trip selection
        effect(() => {
          const route = routerState.currentRoute;
          if (route) {
            this.handleRouteChange(route);
          }
        }),
        // Effect 2: Update page title when current trip changes
        effect(() => {
          const trip = this.current;
          if (trip) {
            routerState.setPageTitle(generatePageTitle(trip.displayName));
          }
        }),
        // Effect 3: Monitor viewport size changes for responsive behavior (via the platform
        // ViewportPort — web reads window/resize, RN reads Dimensions).
        effect(() => {
          // Initial detection
          this.updateMobileDetection();

          // Subscribe to viewport changes; the returned unsubscribe is the effect cleanup.
          return viewport.subscribe(() => this.updateMobileDetection());
        }),
        // Effect 4: Watch privilegedTripHandle for privileged access to other users' trips
        effect(() => {
          const handle = this.privilegedTripHandle;
          if (handle?.isReady && handle.data) {
            // Guard: Verify we're still on the trip route for this trip ID
            // This prevents re-setting this.current if user navigated away while loading
            const currentRoute = routerState.currentRoute;
            const isTripRoute = currentRoute && isTripPath(currentRoute.path);
            const routeTripId = currentRoute?.params?.id;

            if (!isTripRoute || routeTripId !== handle.data.id) {
              console.debug(
                `[TripsState] Skipping privileged trip load - user navigated away (route: ${currentRoute?.path}, tripId: ${routeTripId}, loaded: ${handle.data.id})`,
              );
              return;
            }

            // Trip loaded successfully via privileged access
            console.info(`[TripsState] Privileged trip loaded: ${handle.data.id}`);
            this.current = handle.data;
          }
        }),
      ];

      this.cleanupFunctions.push(...effects);

      // Store and return cleanup function reference
      this.cleanupFunctionRef = () => this.stopMonitors();
      return this.cleanupFunctionRef;
    },

    /** Clean up all effects to prevent memory leaks */
    stopMonitors() {
      for (const cleanup of this.cleanupFunctions) cleanup();
      this.cleanupFunctions.length = 0; // Clear the array
      this.cleanupFunctionRef = undefined; // Clear the reference
      // Clean up privileged trip handle to prevent Firestore subscription leak
      this.cleanupPrivilegedTripHandle();
    },
  });

  return state;
}

/** The main reactive store for all core trip functionality. All properties are signals transparent to the user. */
export type TripsState = ReturnType<typeof createTripsState>;
